import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiClient } from '@/lib/api';
import { PERSONAL_ASSISTANT_UPDATED_EVENT, type PersonalAssistantSectionRequest } from '@/lib/personal-assistant-events';
import { useAuth } from './AuthContext';

interface MyAssistant {
  id: string;
  name: string;
  description?: string | null;
  display_name?: string;
  avatar_icon?: string;
  color_theme?: string;
  is_active: boolean;
  assigned_user_first_name?: string | null;
}

interface PersonalAssistantAccess {
  assistant: MyAssistant | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  sectionRequest: PersonalAssistantSectionRequest | undefined;
  openRoutines: () => void;
}

const PersonalAssistantAccessContext = createContext<PersonalAssistantAccess | null>(null);

function ScopedPersonalAssistantAccess({ children }: { children: ReactNode }) {
  const [assistant, setAssistant] = useState<MyAssistant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sectionRequest, setSectionRequest] = useState<PersonalAssistantSectionRequest>();
  const generation = useRef(0);
  const requestId = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<MyAssistant>('/personal-assistant/my-assistant');
      if (current !== generation.current) return;
      if (!response.success) throw new Error('Impossibile caricare il tuo assistente personale.');
      setAssistant(response.data?.id ? response.data : null);
    } catch (cause) {
      if (current !== generation.current) return;
      setAssistant(null);
      setError(cause instanceof Error ? cause.message : 'Impossibile caricare il tuo assistente personale.');
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
    const onUpdated = () => { void refresh(); };
    window.addEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, onUpdated);
    return () => {
      generation.current += 1;
      window.removeEventListener(PERSONAL_ASSISTANT_UPDATED_EVENT, onUpdated);
    };
  }, [refresh]);
  const openRoutines = useCallback(() => {
    setSectionRequest({ id: ++requestId.current, section: 'routines' });
  }, []);
  return (
    <PersonalAssistantAccessContext.Provider value={{ assistant, loading, error, refresh, sectionRequest, openRoutines }}>
      {children}
    </PersonalAssistantAccessContext.Provider>
  );
}

export function PersonalAssistantAccessProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <ScopedPersonalAssistantAccess key={`${user?.tenantId ?? user?.tenantSlug ?? ''}:${user?.id ?? ''}`}>
      {children}
    </ScopedPersonalAssistantAccess>
  );
}

export function usePersonalAssistantAccess(): PersonalAssistantAccess {
  const value = useContext(PersonalAssistantAccessContext);
  if (!value) throw new Error('PersonalAssistantAccessProvider non disponibile');
  return value;
}